import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Alert, Box, Button, Chip, CircularProgress, Container, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, MenuItem, TextField, Tooltip, Typography, useMediaQuery } from "@mui/material";
import { CheckCircle, Close, Description, DescriptionOutlined, FileDownload, Folder, Print, Receipt, Undo } from "@mui/icons-material";
import { useAuth } from "../contexts/AuthContext";
import HeaderInternal from "../components/Header/HeaderInternal";
import { FooterInternal } from "../components/Footer";
import { FaqButton } from "../components/FaqButton";
import ClearFiltersButton from "../components/ClearFiltersButton";
import ResultCount from "../components/ResultCount";
import ReportSummaryCard, { summaryCardsRowSx } from "../components/Reports/ReportSummaryCard";
import MonthSelect from "../components/Inputs/MonthSelect";
import StandardPagination, { ITENS_POR_PAGINA_PADRAO } from "../components/Pagination/StandardPagination";
import { redirectToLogin } from "../services/httpClient";
import { receiptsReportService, ReceiptsReport, ReceiptsReportRow, ReceiptsSituation } from "../services/receiptsReportService";
import { useNavigation } from "../contexts/RouterContext";
import { filterFieldSx } from "../theme/filterStyles";
import { colors, pageHeader, typography } from "../theme/designSystem";

/**
 * Recebimentos (RN-544): o que entrou pelo caixa do paciente no período, por forma de pagamento e
 * por dia. Lê patient_payments — não é o livro-caixa. Estornos ficam fora do total e aparecem pelo
 * filtro Situação. Mesmo padrão dos relatórios: competência + De/Até, KPIs, donut, tabela, Excel.
 */
const PIE_COLORS = ["#03B4C6", "#512E5F", "#FFCC00", "#FF6347", "#4caf50", "#adb5bd", "#2196f3", "#e91e63"];

const localIso = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const currentCompetence = () => localIso(new Date()).slice(0, 7);
const competenceBounds = (competence: string) => {
  const [y, m] = competence.split("-").map(Number);
  return { from: localIso(new Date(y, m - 1, 1)), to: localIso(new Date(y, m, 0)) };
};
const brl = (v: number) => v.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
const fmtDate = (iso: string) => `${iso.slice(8, 10)}/${iso.slice(5, 7)}/${iso.slice(0, 4)}`;
const fmtTime = (iso: string) => iso.slice(11, 16);

const SITUATIONS: { value: ReceiptsSituation; label: string }[] = [
  { value: "unchecked", label: "Não conferidos" },
  { value: "checked", label: "Conferidos" },
  { value: "reverted", label: "Estornados" },
  { value: "all", label: "Todos" },
];
const DEFAULT_SITUATION: ReceiptsSituation = "unchecked";

const situationChip = (r: ReceiptsReportRow) => r.reverted
  ? { label: "Estornado", bg: colors.errorSoft, color: colors.error }
  : r.conciliationChecked
    ? { label: "Conferido", bg: colors.successSoft, color: "#155724" }
    : { label: "Não conferido", bg: colors.warningSoft, color: colors.warningSoftText };

const ReportReceipts: React.FC = () => {
  const { goToDashboard, goToPatientRegister } = useNavigation();
  const { user } = useAuth();
  const isAdmin = (user?.role || "").toLowerCase() === "administrador";
  // Abaixo de 768px a lista vira cartão próprio (padrão do sistema), não a tabela com data-label
  const isMobile = useMediaQuery("(max-width:768px)");
  const initial = competenceBounds(currentCompetence());

  const [from, setFrom] = useState(initial.from);
  const [to, setTo] = useState(initial.to);
  const [methodId, setMethodId] = useState("");
  const [professionalId, setProfessionalId] = useState("");
  const [situation, setSituation] = useState<ReceiptsSituation>(DEFAULT_SITUATION);
  const [data, setData] = useState<ReceiptsReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(ITENS_POR_PAGINA_PADRAO);
  const [details, setDetails] = useState<ReceiptsReportRow | null>(null);

  // Competência deriva das datas: mês inteiro selecionado = competência; qualquer outra faixa = vazio
  const competence = useMemo(() => {
    if (from.length < 10 || to.length < 10 || from.slice(0, 7) !== to.slice(0, 7)) return "";
    const b = competenceBounds(from.slice(0, 7));
    return from === b.from && to === b.to ? from.slice(0, 7) : "";
  }, [from, to]);
  const applyCompetence = (value: string) => {
    if (!value) return;
    const b = competenceBounds(value);
    setFrom(b.from);
    setTo(b.to);
  };

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setData(await receiptsReportService.get({ from, to, methodId, professionalId, situation }));
    } catch {
      setError("Não foi possível carregar os recebimentos.");
    } finally {
      setLoading(false);
    }
  }, [from, to, methodId, professionalId, situation]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setCurrentPage(1); }, [from, to, methodId, professionalId, situation]);

  const hasFilters = from !== initial.from || to !== initial.to || methodId !== "" || professionalId !== "" || situation !== DEFAULT_SITUATION;
  const clearFilters = () => { setFrom(initial.from); setTo(initial.to); setMethodId(""); setProfessionalId(""); setSituation(DEFAULT_SITUATION); };

  const rows = data?.items ?? [];
  const totalPages = Math.max(1, Math.ceil(rows.length / itemsPerPage));
  const paged = useMemo(() => rows.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage), [rows, currentPage, itemsPerPage]);

  const donut = useMemo(() => {
    const items = data?.byMethod ?? [];
    const total = items.reduce((t, i) => t + i.total, 0);
    let acc = 0;
    return items.map((item, index) => {
      const percent = total > 0 ? (100 * item.total) / total : 0;
      const seg = { ...item, percent, offset: -acc, color: PIE_COLORS[index % PIE_COLORS.length] };
      acc += percent;
      return seg;
    });
  }, [data]);

  const maxDay = useMemo(() => Math.max(0, ...(data?.byDay ?? []).map((d) => d.total)), [data]);

  const handleExport = async () => {
    if (exporting) return;
    setExporting(true);
    try {
      const blob = await receiptsReportService.exportXlsx({ from, to, methodId, professionalId, situation });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `recebimentos_${from}_${to}.xlsx`;
      link.click();
      URL.revokeObjectURL(url);
    } catch {
      setError("Não foi possível exportar a planilha. Tente novamente.");
    } finally {
      setExporting(false);
    }
  };

  const openReceipt = (paymentId: string) => {
    window.open(`${window.location.pathname}?page=payment-receipt&id=${paymentId}`, "_blank", "noopener");
  };

  const handleRevalidateLogin = () => {
    localStorage.removeItem("clinic4us-user-session");
    localStorage.removeItem("clinic4us-remember-me");
    redirectToLogin();
  };

  const methodsLabel = (r: ReceiptsReportRow) => {
    if (!r.method1Name) return "—";
    if (!r.method2Name) return r.method1Name;
    return `${r.method1Name} ${brl(r.value1 ?? 0)} · ${r.method2Name} ${brl(r.value2 ?? 0)}`;
  };

  const sectionSx = { background: "white", border: `1px solid ${colors.borderLight}`, borderRadius: "8px", p: 2 };

  return (
    <Box className="c4u-print-page" sx={{ display: "flex", flexDirection: "column", minHeight: "100vh" }}>
      <HeaderInternal showCTAButton={false} className="login-header" isLoggedIn={true} notificationCount={0} onRevalidateLogin={handleRevalidateLogin} onLogoClick={goToDashboard} />

      <Box component="main" className="c4u-main">
        <Container maxWidth={false} disableGutters sx={pageHeader.container}>
          <Box className="c4u-page-header c4u-page-header--in-panel">
            <Box>
              <Typography variant="h4" className="c4u-page-header__title">Recebimentos e conciliação</Typography>
              <Typography variant="body2" className="c4u-page-header__subtitle">
                O que entrou pelo caixa do paciente no período, por forma de pagamento e por dia, com a conferência de cada recebimento. Estornos ficam fora do total.
              </Typography>
            </Box>
            <Box className="c4u-page-header__actions">
              <FaqButton />
            </Box>
          </Box>

          <div className="c4u-panel">
            <div className="c4u-filters__row no-print">
              <div className="c4u-filters__fields">
                <MonthSelect label="Competência" value={competence} onChange={applyCompetence} sx={filterFieldSx} />
                <TextField size="small" type="date" label="De" value={from} onChange={(e) => setFrom(e.target.value)} InputLabelProps={{ shrink: true }} sx={filterFieldSx} />
                <TextField size="small" type="date" label="Até" value={to} onChange={(e) => setTo(e.target.value)} InputLabelProps={{ shrink: true }} sx={filterFieldSx} />
                <TextField select size="small" label="Forma de pagamento" value={methodId} onChange={(e) => setMethodId(e.target.value)} InputLabelProps={{ shrink: true }} SelectProps={{ displayEmpty: true }} sx={filterFieldSx}>
                  <MenuItem value="">Todas</MenuItem>
                  {(data?.methods ?? []).map((m) => <MenuItem key={m.id} value={m.id}>{m.name}</MenuItem>)}
                </TextField>
                <TextField select size="small" label="Profissional" value={professionalId} onChange={(e) => setProfessionalId(e.target.value)} InputLabelProps={{ shrink: true }} SelectProps={{ displayEmpty: true }} sx={filterFieldSx}>
                  <MenuItem value="">Todos</MenuItem>
                  {(data?.professionals ?? []).map((p) => <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>)}
                </TextField>
                <TextField select size="small" label="Situação" value={situation} onChange={(e) => setSituation(e.target.value as ReceiptsSituation)} InputLabelProps={{ shrink: true }} sx={filterFieldSx}>
                  {SITUATIONS.map((s) => <MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>)}
                </TextField>
              </div>
              <div className="c4u-filters__actions">
                <ClearFiltersButton onClick={clearFilters} disabled={!hasFilters || loading} />
                <Tooltip title="Exportar para Excel (pagamentos e resumo)">
                  <IconButton className="c4u-btn-icon c4u-btn-icon--outline" onClick={handleExport} disabled={loading || exporting || rows.length === 0} aria-label="Exportar para Excel">
                    {exporting ? <CircularProgress size={16} sx={{ color: colors.primary }} /> : <FileDownload fontSize="small" />}
                  </IconButton>
                </Tooltip>
                <Tooltip title="Imprimir">
                  <IconButton className="c4u-btn-icon c4u-btn-icon--outline" onClick={() => window.print()} disabled={loading || rows.length === 0} aria-label="Imprimir">
                    <Print fontSize="small" />
                  </IconButton>
                </Tooltip>
              </div>
            </div>

            {error && (
              <Box className="c4u-info-box c4u-info-box--warning" sx={{ mb: 2 }}>
                <Typography>{error}</Typography>
              </Box>
            )}

            {loading || !data ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
                <CircularProgress size={28} sx={{ color: colors.primary }} />
              </Box>
            ) : (
              <>
                <Box sx={summaryCardsRowSx}>
                  <ReportSummaryCard label="Recebido no período" value={brl(data.total)} color={colors.primary} sub={`${data.count} pagamento(s) ativo(s)`} />
                  <ReportSummaryCard label="Ticket médio" value={brl(data.average)} sub="por pagamento" />
                  <ReportSummaryCard label="Estornado no período" value={brl(data.revertedTotal)} color={data.revertedCount > 0 ? colors.error : undefined} sub={`${data.revertedCount} estorno(s)`} />
                  <ReportSummaryCard label="A conferir" value={brl(data.toCheckTotal)} color={data.toCheckCount > 0 ? colors.warningSoftText : colors.success} sub={`${data.toCheckCount} pagamento(s) sem conferência`} />
                </Box>

                <Box sx={{ display: "flex", gap: 3, flexWrap: "wrap", mb: 3 }}>
                  <Box sx={{ ...sectionSx, flex: "1 1 340px" }}>
                    <Typography sx={{ fontSize: 15, fontWeight: typography.fontWeight.semibold, mb: 1 }}>Por forma de pagamento</Typography>
                    {donut.length === 0 ? (
                      <Typography sx={{ color: colors.textSecondary, fontSize: "0.875rem" }}>Nenhum recebimento no período.</Typography>
                    ) : (
                      <Box sx={{ display: "flex", gap: { xs: 2, sm: 3 }, alignItems: "center", flexDirection: { xs: "column", sm: "row" } }}>
                        <Box sx={{ position: "relative", width: 140, height: 140, flexShrink: 0, mx: "auto" }}>
                          <svg width="140" height="140" viewBox="0 0 42 42" style={{ transform: "rotate(-90deg)" }}>
                            <circle cx="21" cy="21" r="15.915" fill="transparent" stroke="#e9ecef" strokeWidth="3" />
                            {donut.map((seg) => (
                              <circle key={seg.method} cx="21" cy="21" r="15.915" fill="transparent" stroke={seg.color} strokeWidth="3"
                                strokeDasharray={`${seg.percent} ${100 - seg.percent}`} strokeDashoffset={seg.offset} strokeLinecap="round" />
                            ))}
                          </svg>
                          <Box sx={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)", textAlign: "center" }}>
                            <Typography sx={{ fontWeight: 700, fontSize: "0.95rem", color: colors.primary }}>{brl(data.total)}</Typography>
                            <Typography sx={{ fontSize: "0.7rem", color: colors.textSecondary }}>{data.count} pag.</Typography>
                          </Box>
                        </Box>
                        <Box sx={{ flex: 1, minWidth: 0, width: "100%", display: "flex", flexDirection: "column", gap: 0.75 }}>
                          {donut.map((seg) => (
                            <Box key={seg.method} sx={{ display: "flex", alignItems: "center", gap: 1, fontSize: "0.85rem" }}>
                              <Box sx={{ width: 10, height: 10, borderRadius: "50%", background: seg.color, flexShrink: 0 }} />
                              <Box sx={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{seg.method}</Box>
                              <Box sx={{ color: colors.textSecondary }}>{seg.count}×</Box>
                              <Box sx={{ fontWeight: 600, whiteSpace: "nowrap" }}>{brl(seg.total)}</Box>
                              <Box sx={{ color: colors.textSecondary, width: 42, textAlign: "right" }}>{seg.percent.toFixed(0)}%</Box>
                            </Box>
                          ))}
                        </Box>
                      </Box>
                    )}
                  </Box>

                  <Box sx={{ ...sectionSx, flex: "2 1 420px", minWidth: 0 }}>
                    <Typography sx={{ fontSize: 15, fontWeight: typography.fontWeight.semibold, mb: 1 }}>Por dia</Typography>
                    {data.byDay.length === 0 ? (
                      <Typography sx={{ color: colors.textSecondary, fontSize: "0.875rem" }}>Nenhum recebimento no período.</Typography>
                    ) : (
                      <Box sx={{ overflowX: "auto" }}>
                        <Box sx={{ display: "flex", alignItems: "flex-end", gap: 0.75, height: 160, minWidth: data.byDay.length * 34, pt: 2 }}>
                          {data.byDay.map((d) => (
                            <Tooltip key={d.day} title={`${fmtDate(d.day)} · ${brl(d.total)} · ${d.count} pagamento(s)`} arrow>
                              <Box sx={{ flex: "1 0 28px", display: "flex", flexDirection: "column", alignItems: "center", gap: 0.5, height: "100%", justifyContent: "flex-end" }}>
                                <Box sx={{ width: "100%", maxWidth: 28, height: `${maxDay > 0 ? Math.max(3, (100 * d.total) / maxDay) : 0}%`, background: colors.primary, borderRadius: "4px 4px 0 0" }} />
                                <Typography sx={{ fontSize: "0.65rem", color: colors.textSecondary, whiteSpace: "nowrap" }}>{d.day.slice(8, 10)}/{d.day.slice(5, 7)}</Typography>
                              </Box>
                            </Tooltip>
                          ))}
                        </Box>
                      </Box>
                    )}
                  </Box>
                </Box>

                <ResultCount shown={rows.length} total={rows.length} />

                {isMobile ? (
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
                    {paged.length === 0 && (
                      <Typography sx={{ color: colors.textSecondary, fontSize: "0.875rem", py: 3, textAlign: "center" }}>Nenhum pagamento no filtro.</Typography>
                    )}
                    {paged.map((r) => (
                      <Box key={r.paymentId} sx={{ border: `1px solid ${colors.borderLight}`, borderRadius: "10px", p: 1.5, background: "#fff", opacity: r.reverted ? 0.75 : 1 }}>
                        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 1 }}>
                          <Box sx={{ fontSize: "0.85rem", color: colors.textSecondary }}>{fmtDate(r.date)} · {fmtTime(r.date)}</Box>
                          <Box sx={{ fontWeight: 700, fontSize: "1.05rem", textDecoration: r.reverted ? "line-through" : "none" }}>{brl(r.total)}</Box>
                        </Box>
                        <Box sx={{ fontWeight: 600, mt: 0.5, overflowWrap: "anywhere" }}>{r.patientName}</Box>
                        <Box sx={{ fontSize: "0.85rem", color: colors.textSecondary, mt: 0.25 }}>{methodsLabel(r)} · {r.sessions} sessão(ões)</Box>
                        {r.notes && <Box sx={{ fontSize: "0.8rem", color: colors.textSecondary, mt: 0.25 }}>{r.notes}</Box>}
                        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, mt: 1 }}>
                          <Chip size="small" label={situationChip(r).label} sx={{ fontWeight: 600, bgcolor: situationChip(r).bg, color: situationChip(r).color }} />
                          <Box sx={{ display: "flex", gap: 0.75 }}>
                            <IconButton size="small" onClick={() => setDetails(r)} aria-label="Dados do pagamento"
                              sx={{ bgcolor: r.invoiceUrl ? "#4caf50" : "#ff9800", color: "white", width: 36, height: 36, "&:hover": { bgcolor: r.invoiceUrl ? "#388e3c" : "#f57c00" } }}>
                              {r.invoiceUrl ? <Description fontSize="small" /> : <DescriptionOutlined fontSize="small" />}
                            </IconButton>
                            <IconButton size="small" onClick={() => openReceipt(r.paymentId)} aria-label="Recibo" sx={{ bgcolor: colors.primary, color: "white", width: 36, height: 36, "&:hover": { bgcolor: colors.primaryHover } }}>
                              <Receipt fontSize="small" />
                            </IconButton>
                            <IconButton size="small" onClick={() => goToPatientRegister(r.patientId)} aria-label="Cadastro do paciente" sx={{ bgcolor: "#03a9f4", color: "white", width: 36, height: 36, "&:hover": { bgcolor: "#0288d1" } }}>
                              <Folder fontSize="small" />
                            </IconButton>
                          </Box>
                        </Box>
                      </Box>
                    ))}
                  </Box>
                ) : (
                <Box className="c4u-table">
                  <Box className="c4u-table__header">
                    <Box className="admin-plans-header-cell" sx={{ flex: "0 0 130px" }}>Data</Box>
                    <Box className="admin-plans-header-cell" sx={{ flex: "2 1 220px", minWidth: 180 }}>Paciente</Box>
                    <Box className="admin-plans-header-cell c4u-col--sm" sx={{ flex: "0 0 80px", textAlign: "center" }}>Sessões</Box>
                    <Box className="admin-plans-header-cell c4u-col--md" sx={{ flex: "2 1 220px", minWidth: 160 }}>Forma de pagamento</Box>
                    <Box className="admin-plans-header-cell" sx={{ flex: "0 0 120px", textAlign: "right" }}>Total</Box>
                    <Box className="admin-plans-header-cell c4u-col--lg" sx={{ flex: "0 0 110px", textAlign: "center" }}>Situação</Box>
                    <Box className="admin-plans-header-cell" sx={{ flex: "0 0 110px", marginLeft: "auto", textAlign: "right" }}>Ações</Box>
                  </Box>

                  {paged.length === 0 && (
                    <Typography sx={{ color: colors.textSecondary, fontSize: "0.875rem", py: 3, textAlign: "center" }}>
                      Nenhum pagamento no filtro.
                    </Typography>
                  )}

                  {paged.map((r) => (
                    <Box key={r.paymentId} className="c4u-table__row" sx={{ opacity: r.reverted ? 0.7 : 1 }}>
                      <Box className="admin-plans-cell" data-label="Data" sx={{ flex: "0 0 130px" }}>
                        <Box sx={{ display: "flex", flexDirection: "column", lineHeight: 1.3 }}>
                          <Box sx={{ fontWeight: 600 }}>{fmtDate(r.date)}</Box>
                          <Box sx={{ fontSize: "0.75rem", color: colors.textSecondary }}>{fmtTime(r.date)}</Box>
                        </Box>
                      </Box>
                      <Box className="admin-plans-cell" data-label="Paciente" sx={{ flex: "2 1 220px", minWidth: 180 }}>
                        <Box>{r.patientName}</Box>
                        {r.notes && <Box sx={{ fontSize: "0.75rem", color: colors.textSecondary }}>{r.notes}</Box>}
                      </Box>
                      <Box className="admin-plans-cell c4u-col--sm" data-label="Sessões" sx={{ flex: "0 0 80px", textAlign: "center" }}>{r.sessions}</Box>
                      <Box className="admin-plans-cell c4u-col--md" data-label="Forma de pagamento" sx={{ flex: "2 1 220px", minWidth: 160, fontSize: "0.85rem" }}>{methodsLabel(r)}</Box>
                      <Box className="admin-plans-cell" data-label="Total" sx={{ flex: "0 0 120px", textAlign: "right", fontWeight: 700, textDecoration: r.reverted ? "line-through" : "none" }}>{brl(r.total)}</Box>
                      <Box className="admin-plans-cell c4u-col--lg" data-label="Situação" sx={{ flex: "0 0 110px", textAlign: "center" }}>
                        <Tooltip title={r.conciliationChecked && r.conciliationCheckedAt ? `Conferido por ${r.conciliationCheckedBy || "—"} em ${fmtDate(r.conciliationCheckedAt)} ${fmtTime(r.conciliationCheckedAt)}` : ""} arrow>
                          <Chip size="small" label={situationChip(r).label} sx={{ fontWeight: 600, bgcolor: situationChip(r).bg, color: situationChip(r).color }} />
                        </Tooltip>
                      </Box>
                      <Box className="admin-plans-cell" data-label="Ações" sx={{ flex: "0 0 110px", marginLeft: "auto", display: "flex", gap: 0.5, justifyContent: "flex-end" }}>
                        <Tooltip title={r.invoiceUrl ? "Dados do pagamento · conferir · NF cadastrada" : "Dados do pagamento · conferir, incluir NF"}>
                          <IconButton className="c4u-row-action" onClick={() => setDetails(r)} size="small"
                            sx={{ bgcolor: r.invoiceUrl ? "#4caf50" : "#ff9800", color: "white", "&:hover": { bgcolor: r.invoiceUrl ? "#388e3c" : "#f57c00" } }}>
                            {r.invoiceUrl ? <Description fontSize="small" /> : <DescriptionOutlined fontSize="small" />}
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Recibo">
                          <IconButton className="c4u-row-action" onClick={() => openReceipt(r.paymentId)} size="small" sx={{ bgcolor: colors.primary, color: "white", "&:hover": { bgcolor: colors.primaryHover } }}>
                            <Receipt fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Abrir cadastro do paciente">
                          <IconButton className="c4u-row-action" onClick={() => goToPatientRegister(r.patientId)} size="small" sx={{ bgcolor: "#03a9f4", color: "white", "&:hover": { bgcolor: "#0288d1" } }}>
                            <Folder fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </Box>
                  ))}
                </Box>
                )}

                {rows.length > 0 && (
                  <StandardPagination currentPage={currentPage} totalPages={totalPages} itemsPerPage={itemsPerPage} totalItems={rows.length}
                    onPageChange={setCurrentPage} onItemsPerPageChange={(n) => { setItemsPerPage(n); setCurrentPage(1); }} />
                )}
              </>
            )}
          </div>
        </Container>
      </Box>

      <PaymentDetailsDialog
        row={details}
        isAdmin={isAdmin}
        onClose={() => setDetails(null)}
        onSaved={() => { setDetails(null); load(); }}
        onOpenReceipt={openReceipt}
      />

      <FooterInternal className="login-footer-component" />
    </Box>
  );
};

/**
 * Dados do pagamento (somente leitura) + duas ações do Recebimentos: conferência (RN-544b) e inclusão
 * do link da NF quando ainda não há (RN-544a). Forma de pagamento não se altera por aqui (é no caixa, RN-543i).
 */
const PaymentDetailsDialog: React.FC<{
  row: ReceiptsReportRow | null;
  isAdmin: boolean;
  onClose: () => void;
  onSaved: () => void;
  onOpenReceipt: (paymentId: string) => void;
}> = ({ row, isAdmin, onClose, onSaved, onOpenReceipt }) => {
  const [url, setUrl] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  useEffect(() => { setUrl(""); setError(""); setSaving(false); }, [row]);
  if (!row) return null;

  const fail = (e: unknown, fallback: string) => {
    const d = (e as { response?: { data?: { description?: string } } })?.response?.data;
    setError(d?.description || fallback);
  };
  const run = async (fn: () => Promise<void>, fallback: string) => {
    setSaving(true);
    setError("");
    try { await fn(); onSaved(); } catch (e) { fail(e, fallback); } finally { setSaving(false); }
  };

  const validUrl = /^https?:\/\/\S+$/i.test(url.trim());
  const saveInvoice = () => run(() => receiptsReportService.setInvoice(row.paymentId, url.trim()), "Não foi possível salvar o link da NF.");
  const setChecked = (checked: boolean) => run(() => receiptsReportService.setConciliation(row.paymentId, checked), "Não foi possível registrar a conferência.");

  const line = (label: string, value: React.ReactNode) => (
    <Box sx={{ display: "grid", gridTemplateColumns: "150px 1fr", gap: 1, py: 0.5, borderBottom: `1px solid ${colors.borderLight}`, fontSize: "0.9rem" }}>
      <Box sx={{ color: colors.textSecondary }}>{label}</Box>
      <Box sx={{ fontWeight: 600, overflowWrap: "anywhere" }}>{value}</Box>
    </Box>
  );
  const sectionTitle = (text: string) => <Typography sx={{ fontSize: "0.9rem", fontWeight: 600, mb: 1 }}>{text}</Typography>;
  const chip = situationChip(row);
  const paymentLabel = row.byInjunction
    ? "Pago por liminar (sem forma de pagamento)"
    : row.method2Name
      ? `${row.method1Name} · ${brl(row.value1 ?? 0)}   +   ${row.method2Name} · ${brl(row.value2 ?? 0)}`
      : `${row.method1Name || "—"} · ${brl(row.total)}`;

  return (
    <Dialog open onClose={saving ? undefined : onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: "12px", boxShadow: "0 8px 32px rgba(0,0,0,0.12)" } }}>
      <DialogTitle sx={{ background: colors.primary, color: "#fff", display: "flex", alignItems: "center", justifyContent: "space-between", padding: "1.25rem 1.5rem", fontSize: "1.1rem", fontWeight: 600 }}>
        Dados do pagamento
        <IconButton onClick={onClose} disabled={saving} sx={{ color: "#fff", padding: "0.25rem" }} aria-label="Fechar"><Close /></IconButton>
      </DialogTitle>
      <DialogContent sx={{ pt: "1.25rem !important" }}>
        {line("Recibo Nº", row.paymentId.slice(0, 8).toUpperCase())}
        {line("Data", `${fmtDate(row.date)} ${fmtTime(row.date)}`)}
        {line("Paciente", row.patientName)}
        {line("Sessões", row.sessions)}
        {line("Total", brl(row.total))}
        {line("Situação", <Chip size="small" label={chip.label} sx={{ fontWeight: 600, bgcolor: chip.bg, color: chip.color }} />)}
        {row.conciliationChecked && line("Conferido", `${row.conciliationCheckedBy || "—"}${row.conciliationCheckedAt ? ` em ${fmtDate(row.conciliationCheckedAt)} ${fmtTime(row.conciliationCheckedAt)}` : ""}`)}
        {row.notes && line("Observação", row.notes)}

        <Box sx={{ mt: 2 }}>
          {sectionTitle("Forma de pagamento")}
          <Typography sx={{ fontSize: "0.95rem" }}>{paymentLabel}</Typography>
        </Box>

        <Box sx={{ mt: 2 }}>
          {sectionTitle("Nota fiscal")}
          {row.invoiceUrl ? (
            <>
              <a href={row.invoiceUrl} target="_blank" rel="noopener noreferrer" style={{ color: colors.primary, overflowWrap: "anywhere" }}>{row.invoiceUrl}</a>
              <Typography sx={{ fontSize: "0.78rem", color: colors.textSecondary, mt: 0.5 }}>Link já cadastrado. Para alterar, use Alterar pagamento no caixa do paciente.</Typography>
            </>
          ) : row.reverted ? (
            <Typography sx={{ fontSize: "0.85rem", color: colors.textSecondary }}>Pagamento estornado: não recebe link de NF.</Typography>
          ) : (
            <Box sx={{ display: "flex", gap: 1, alignItems: "flex-start", flexWrap: "wrap" }}>
              <TextField size="small" label="Link da nota fiscal (NF)" placeholder="https://…" value={url} onChange={(e) => setUrl(e.target.value)}
                InputLabelProps={{ shrink: true }} inputProps={{ maxLength: 500 }} sx={{ ...filterFieldSx, flex: "1 1 260px" }} helperText="Só inclusão: depois de salvo, o link não muda por esta tela." FormHelperTextProps={{ sx: { mx: 0 } }} />
              <Button variant="outlined" onClick={saveInvoice} disabled={!validUrl || saving} startIcon={<Description />} sx={{ textTransform: "none", height: 40, borderColor: colors.primary, color: colors.primary }}>Salvar NF</Button>
            </Box>
          )}
        </Box>

        {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
      </DialogContent>
      <DialogActions sx={{ padding: "1rem 1.5rem", gap: 1, flexWrap: "wrap" }}>
        <Button onClick={() => onOpenReceipt(row.paymentId)} variant="outlined" startIcon={<Receipt />} sx={{ textTransform: "none", borderColor: colors.border, color: colors.textSecondary, mr: "auto" }}>Recibo</Button>
        <Button onClick={onClose} variant="outlined" disabled={saving} sx={{ textTransform: "none", borderColor: colors.border, color: colors.textSecondary }}>Fechar</Button>
        {!row.reverted && !row.conciliationChecked && (
          <Button onClick={() => setChecked(true)} variant="contained" disabled={saving} startIcon={saving ? <CircularProgress size={16} sx={{ color: "#fff" }} /> : <CheckCircle />}
            sx={{ textTransform: "none", fontWeight: 600, bgcolor: colors.success, "&:hover": { bgcolor: colors.successHover }, boxShadow: "none" }}>
            Marcar como conferido
          </Button>
        )}
        {!row.reverted && row.conciliationChecked && isAdmin && (
          <Tooltip title="Só o Administrador desfaz a conferência" arrow>
            <Button onClick={() => setChecked(false)} variant="outlined" disabled={saving} startIcon={<Undo />} sx={{ textTransform: "none", borderColor: colors.border, color: colors.textSecondary }}>
              Desfazer conferência
            </Button>
          </Tooltip>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ReportReceipts;
